import axios from "axios";

// Constants
const API_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string for relative URLs in production
  : "http://localhost:5500";
const TOKEN_KEY = "token";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30-second timeout
});

// Define a variable to store the unauthorized handler
let unauthorizedHandler = () => {
  console.error("Unauthorized access - no handler set");
};

// Function to update the unauthorized handler
const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = handler;
};

// Request interceptor with enhanced error handling
api.interceptors.request.use(
  (config) => {
    // Add auth header if token exists
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log outgoing requests in development
    if (process.env.NODE_ENV === "development") {
      console.log("API Request:", {
        url: config.url,
        method: config.method,
        data: config.data,
        headers: config.headers,
      });
    }

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject({
      message: "Error preparing request",
      originalError: error,
    });
  }
);

// Response interceptor with comprehensive error handling
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === "development") {
      console.log("API Response:", {
        status: response.status,
        data: response.data,
        url: response.config.url,
      });
    }
    return response;
  },
  (error) => {
    // Handle network errors
    if (!error.response) {
      console.error("Network/Timeout error:", error);
      return Promise.reject({
        success: false,
        message: "Network error - please check your connection",
        originalError: error,
      });
    }

    // Log error details in development
    if (process.env.NODE_ENV === "development") {
      console.error("API Error:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
      });
    }

    // Handle specific error cases
    switch (error.response.status) {
      case 400:
        console.error("Bad request:", error.response.data);
        break;
      case 401:
        console.error("Unauthorized access");
        unauthorizedHandler(); // Call the handler
        break;
      case 403:
        console.error("Permission denied");
        break;
      case 404:
        console.error("Resource not found");
        break;
      case 422:
        console.error("Validation error:", error.response.data);
        break;
      case 429:
        console.error("Too many requests");
        break;
      case 500:
        console.error("Server error");
        break;
      default:
        console.error("Unhandled error status:", error.response.status);
    }

    return Promise.reject({
      success: false,
      message: error.response?.data?.message || "An error occurred",
      status: error.response?.status,
      data: error.response?.data,
      originalError: error,
    });
  }
);

// Utility function to handle API errors
const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error
    return {
      success: false,
      message: error.response.data?.message || "Server error occurred",
      status: error.response.status,
      data: error.response.data,
    };
  } else if (error.request) {
    // Request made but no response
    return {
      success: false,
      message: "No response from server",
      status: 0,
    };
  } else {
    // Error in request configuration
    return {
      success: false,
      message: error.message || "Error preparing request",
      status: 0,
    };
  }
};

// Custom error class for API errors
class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export { api, handleApiError, ApiError, TOKEN_KEY, setUnauthorizedHandler };

// At the end of the file - add the default export
const apiService = { 
  api, 
  handleApiError, 
  ApiError, 
  TOKEN_KEY, 
  setUnauthorizedHandler 
};
export default apiService;